import React, { useEffect, useRef, useState } from "react";
import { toLatLng } from "../../../../utils/geo";

/**
 * The map under the plan (D-23): a PASSIVE MapLibre map that follows IoT3's
 * own viewport, which stays the camera. Nothing on the map is interactive;
 * every gesture still goes to the canvas, and on every viewport change the
 * map is jumped to match it.
 *
 * Angles here are COMPASS bearings (clockwise from north, D-28), the one
 * place outside utils/geo.js that uses them. The site's +y axis points at
 * bearing θ = geoRef.rotationDeg and is drawn straight up on the canvas, so
 * the map is turned to bearing θ underneath an upright plan.
 *
 * MapLibre is lazy-loaded with import(): its own chunk, fetched only when a
 * backdrop actually mounts (the 地圖 switch is on).
 */

export const EARTH_CIRCUMFERENCE_M = 40075016.686;
// MapLibre's zoom 0 draws the world 512 px wide.
const WORLD_PX_AT_ZOOM_0 = 512;

/**
 * jumpTo options for a viewport: the site point under the canvas centre, the
 * zoom whose metres-per-pixel at that latitude equals the viewport's, and the
 * site's bearing.
 *
 * Web Mercator tiles of WGS84 coordinates stretch north-south by ~M/N
 * (0.6 % at 24 N) against east-west, so plan and map agree exactly at the
 * centre and drift by at most that fraction of the distance from it.
 */
export const mapCamera = ({ geoRef, viewport, canvasSize }) => {
  const centre = viewport.unproject({
    x: canvasSize.width / 2,
    y: canvasSize.height / 2,
  });
  const [lat, lng] = toLatLng(geoRef, centre.x, centre.y);
  const metresPerWorld =
    EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180);
  return {
    center: [lng, lat],
    zoom: Math.log2(
      (metresPerWorld * viewport.pxPerMeter) / WORLD_PX_AT_ZOOM_0,
    ),
    bearing: geoRef.rotationDeg,
  };
};

const activeLayer = (tileLayers, layerId) =>
  tileLayers.find((layer) => layer.id === layerId) || tileLayers[0];

/** A style with the one active raster layer (from the site's geo_tile_layers). */
export const mapStyle = (tileLayers, layerId) => {
  const layer = activeLayer(tileLayers, layerId);
  return {
    version: 8,
    sources: {
      [layer.id]: {
        type: "raster",
        tiles: [layer.url],
        tileSize: 256,
        maxzoom: layer.maxzoom ?? 19,
      },
    },
    layers: [{ id: layer.id, type: "raster", source: layer.id }],
  };
};

/**
 * MapLibre >= 3 needs WebGL2. Probed once per page with a throwaway canvas
 * whose context is released straight away; `reset` exists for tests.
 */
let webgl2 = null;
export const hasWebGL2 = () => {
  if (webgl2 === null) {
    try {
      const gl = document.createElement("canvas").getContext("webgl2");
      webgl2 = !!gl;
      gl?.getExtension?.("WEBGL_lose_context")?.loseContext();
    } catch (error) {
      webgl2 = false;
    }
  }
  return webgl2;
};
hasWebGL2.reset = () => {
  webgl2 = null;
};

const loadMaplibre = () =>
  Promise.all([
    import(/* webpackChunkName: "maplibre" */ "maplibre-gl"),
    import(
      /* webpackChunkName: "maplibre" */ "maplibre-gl/dist/maplibre-gl.css"
    ),
  ]).then(([mod]) => mod.default || mod);

const cornerText = {
  position: "absolute",
  right: 4,
  bottom: 2,
  fontSize: 10,
  lineHeight: "14px",
  padding: "0 4px",
  borderRadius: 2,
  color: "#434343",
  backgroundColor: "rgba(255, 255, 255, 0.75)",
};

/**
 * @param {{lat:number,lng:number,rotationDeg:number}} geoRef
 * @param {object[]} tileLayers  the site's geo_tile_layers
 * @param {string} layerId       active tile layer
 * @param {number} opacity       0..1, applied to the map only
 * @param {object} viewport      the canvas's createViewport() result
 * @param {{width:number,height:number}} canvasSize
 * @param {Function} onUnavailable  no WebGL2, or the map failed to start
 */
const GeoBackdrop = ({
  geoRef,
  tileLayers,
  layerId,
  opacity = 1,
  viewport,
  canvasSize,
  onUnavailable,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapVersion, setMapVersion] = useState(0);
  const [supported] = useState(hasWebGL2);
  const latestRef = useRef(null);
  latestRef.current = {
    geoRef,
    tileLayers,
    layerId,
    viewport,
    canvasSize,
    onUnavailable,
  };
  const camera = mapCamera({ geoRef, viewport, canvasSize });

  useEffect(() => {
    if (!supported) {
      latestRef.current.onUnavailable?.();
      return undefined;
    }
    let cancelled = false;
    loadMaplibre()
      .then((maplibre) => {
        if (cancelled || !containerRef.current) return;
        const { tileLayers: layers, layerId: id } = latestRef.current;
        mapRef.current = new maplibre.Map({
          container: containerRef.current,
          interactive: false,
          attributionControl: false,
          style: mapStyle(layers, id),
          fadeDuration: 0,
        });
        setMapVersion((v) => v + 1);
      })
      .catch((error) => {
        console.error("地圖載入失敗:", error);
        if (!cancelled) latestRef.current.onUnavailable?.();
      });
    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [supported]);

  // A new tile layer is a style swap on the same map, not a new map.
  const styledLayerRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (styledLayerRef.current !== null && styledLayerRef.current !== layerId) {
      map.setStyle(mapStyle(latestRef.current.tileLayers, layerId));
    }
    styledLayerRef.current = layerId;
  }, [layerId, mapVersion]);

  useEffect(() => {
    mapRef.current?.resize();
  }, [canvasSize.width, canvasSize.height, mapVersion]);

  const { center, zoom, bearing } = camera;
  useEffect(() => {
    mapRef.current?.jumpTo({ center, zoom, bearing });
    // center is a fresh array each render; its numbers are the real deps.
  }, [center[0], center[1], zoom, bearing, mapVersion]);

  if (!supported) return null;
  const credit = activeLayer(tileLayers, layerId)?.attribution;
  return (
    <>
      <div
        ref={containerRef}
        data-testid="geo-map"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: canvasSize.width,
          height: canvasSize.height,
          opacity,
          pointerEvents: "none",
        }}
      />
      {/* Upright overlays, above the plan and the grid (zIndex) but never
          taking a pointer. The arrow points at true north: -θ on screen. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: canvasSize.width,
          height: canvasSize.height,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <div
          aria-label="北"
          style={{
            position: "absolute",
            right: 8,
            bottom: 20,
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            transform: `rotate(${-bearing}deg)`,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3 L16 13 L12 11 L8 13 Z" fill="#cf1322" />
            <text x="12" y="21" textAnchor="middle" fontSize="7" fill="#262626">
              N
            </text>
          </svg>
        </div>
        {credit && <div style={cornerText}>{credit}</div>}
      </div>
    </>
  );
};

export default GeoBackdrop;
